/**
 * Table designer (ADR-0010 P4.2) — the schema editor for ONE Data Table, as a
 * reusable, routing-agnostic component (props, not `useParams`).
 *
 * It's the grid-first surface (the data IS the surface; fields are edited from the
 * grid column-header menus) + a collapsed "Advanced" drawer for UNIQUE constraints.
 * Two callers mount the SAME body: the standalone `TableDesignerPage` (route shell)
 * and the in-editor `AppTablesPane` (the single-IDE "Dati" rail area, ADR-0048) —
 * so there is ONE designer, no fork.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronRight, KeyRound, Plus, ShieldCheck, Table2, Trash2 } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EmptyState } from "@/components/ui/page";
import { Switch } from "@/components/ui/switch";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { appsApi, type ColumnIn, type ColumnOut, type UniqueSetIn } from "@/lib/api-apps";
import { buildColumnPayload } from "@/lib/columnPayload";
import { FilterRowsEditor, type FilterRow } from "@/components/apps/FilterRowsEditor";
import { DataSection } from "@/components/apps/DataSection";

/** System stamp columns (D16) — present on every table, not editable
 *  through the designer. Mirrors `hub.apps.ddl.SYSTEM_COLUMNS`. */
const SYSTEM_COLUMNS = new Set(["id", "created_at", "updated_at", "created_by", "updated_by"]);

export function TableDesigner({
  appId,
  tableId,
  canWrite,
}: {
  appId: string;
  tableId: string;
  canWrite: boolean;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const [addOpen, setAddOpen] = React.useState(false);
  const [uniqueOpen, setUniqueOpen] = React.useState(false);
  // The table screen is grid-first (Airtable-style): schema editing happens in the
  // grid headers, and these table-level DB features (UNIQUE constraints + row-level
  // security) live in a collapsed "Advanced" drawer so they don't crowd the data.
  const [advancedOpen, setAdvancedOpen] = React.useState(false);

  const tableQ = useQuery({
    queryKey: ["apps", appId, "tables", tableId],
    queryFn: () => appsApi.getTable(appId, tableId),
    enabled: !!appId && !!tableId,
  });

  const renameCol = useMutation({
    mutationFn: ({ columnId, name }: { columnId: string; name: string }) =>
      appsApi.renameColumn(appId, tableId, columnId, name),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["apps", appId, "tables", tableId] }),
  });

  const dropCol = useMutation({
    mutationFn: (columnId: string) => appsApi.dropColumn(appId, tableId, columnId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["apps", appId, "tables", tableId] }),
  });

  const dropSet = useMutation({
    mutationFn: (setId: string) => appsApi.deleteUniqueSet(appId, tableId, setId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["apps", appId, "tables", tableId] }),
  });
  if (tableQ.isError) {
    return (
      <Alert tone="danger" title={t("apps.loadTableFailed", "Could not load table")}>
        <span className="font-mono text-xs">{(tableQ.error as Error).message}</span>
      </Alert>
    );
  }

  const table = tableQ.data;
  const columns = table?.columns ?? [];
  const userColumns = columns.filter((c) => !SYSTEM_COLUMNS.has(c.name));
  const uniqueSets = table?.unique_sets ?? [];
  const colName = (id: string) => columns.find((c) => c.id === id)?.name ?? id;

  // Airtable-style schema edits, driven from the grid column-header menus.
  const renameColumn = (col: ColumnOut) => {
    const name = window.prompt(t("apps.renameFieldPrompt", "New field name"), col.name);
    if (name && name.trim() && name.trim() !== col.name) {
      renameCol.mutate({ columnId: col.id, name: name.trim() });
    }
  };
  const dropColumn = (col: ColumnOut) => {
    if (
      confirm(
        t("apps.confirmDropColumn", 'Drop column "{{name}}"? Its data is lost.', { name: col.name }),
      )
    ) {
      dropCol.mutate(col.id);
    }
  };

  return (
    <div className="space-y-6">
      {/* Grid-first (Airtable-style): the data IS the surface; fields are edited
          from the column-header menus + the toolbar "Field" button. */}
      {table && userColumns.length > 0 ? (
        <DataSection
          appId={appId}
          tableId={tableId}
          columns={userColumns}
          editable={
            canWrite
              ? { onRename: renameColumn, onDrop: dropColumn, onAddField: () => setAddOpen(true) }
              : undefined
          }
        />
      ) : (
        <div className="space-y-3">
          <EmptyState
            icon={Table2}
            title={t("apps.noFields", "No fields yet")}
            description={t(
              "apps.noFieldsDescription",
              "Add your first field to start shaping this table, then enter data.",
            )}
          />
          {canWrite && (
            <div className="flex justify-center">
              <Button onClick={() => setAddOpen(true)}>
                <Plus className="h-4 w-4" strokeWidth={1.5} />
                {t("apps.addField", "Field")}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Advanced — table-level DB features kept out of the data surface:
          UNIQUE constraints + row-level security. Collapsed by default. */}
      {table && (
        <section className="rounded-lg border border-border/60">
          <button
            type="button"
            onClick={() => setAdvancedOpen((o) => !o)}
            aria-expanded={advancedOpen}
            className="flex w-full items-center gap-2 px-4 py-3 text-sm font-semibold text-muted-foreground hover:text-foreground"
          >
            <ChevronRight
              className={`h-4 w-4 transition-transform ${advancedOpen ? "rotate-90" : ""}`}
              strokeWidth={1.5}
            />
            {t("apps.advancedTable", "Advanced — constraints & security")}
          </button>
          {advancedOpen && (
            <div className="space-y-8 border-t border-border/60 p-4">
              {/* Unique sets */}
              <section className="space-y-2">
                <div className="flex items-center justify-between">
                  <h3 className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
                    <KeyRound className="h-4 w-4" strokeWidth={1.5} />
                    {t("apps.uniqueSets", "Unique constraints")}
                  </h3>
                  {canWrite && userColumns.length > 0 && (
                    <Button variant="ghost" size="sm" onClick={() => setUniqueOpen(true)}>
                      <Plus className="h-4 w-4" strokeWidth={1.5} />
                      {t("apps.addUniqueSet", "Add constraint")}
                    </Button>
                  )}
                </div>
                {uniqueSets.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    {t(
                      "apps.noUniqueSetsShort",
                      "No unique constraints. Add one to enable upsert on those columns.",
                    )}
                  </p>
                ) : (
                  <div className="overflow-hidden rounded-lg border border-border/60">
                    <Table>
                      <Thead>
                        <Tr>
                          <Th>{t("apps.constraintName", "Name")}</Th>
                          <Th>{t("apps.columns", "Columns")}</Th>
                          {canWrite && (
                            <Th className="w-16 text-right">{t("common.actions", "Actions")}</Th>
                          )}
                        </Tr>
                      </Thead>
                      <Tbody>
                        {uniqueSets.map((u) => (
                          <Tr key={u.id}>
                            <Td className="font-medium">{u.name}</Td>
                            <Td className="font-mono text-xs">
                              {u.column_ids.map(colName).join(", ")}
                            </Td>
                            {canWrite && (
                              <Td className="text-right">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  aria-label={t("common.delete", "Delete")}
                                  onClick={() => dropSet.mutate(u.id)}
                                >
                                  <Trash2 className="h-3.5 w-3.5 text-danger" strokeWidth={1.5} />
                                </Button>
                              </Td>
                            )}
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </div>
                )}
              </section>

              {/* Row-Level security moved to the per-role "Data access" matrix
                  in Members & roles (ADR-0028 N1 v2) — single home for who sees
                  what (rows / columns / writes) AND the assistant inheritance. */}
              <section className="space-y-2">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
                  <ShieldCheck className="h-4 w-4" strokeWidth={1.5} />
                  {t("apps.rowSecurity", "Row-level security")}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {t(
                    "apps.rowSecurityMovedHint",
                    "Row, column and write access is now managed per role in the Data access matrix.",
                  )}{" "}
                  <button
                    type="button"
                    className="font-medium text-accent underline-offset-2 hover:underline"
                    onClick={() => navigate(`/apps/${appId}/access`)}
                  >
                    {t("apps.manageAccessInMembers", "Manage access in Members & roles")}
                  </button>
                </p>
              </section>

              {/* REST exposure moved to the app's REST interface (ADR-0051, the
                  single exposure registry) — a table is exposed there per-verb,
                  no per-table scaffold. */}
            </div>
          )}
        </section>
      )}

      <AddColumnDialog appId={appId} tableId={tableId} open={addOpen} onOpenChange={setAddOpen} />
      <AddUniqueSetDialog
        appId={appId}
        tableId={tableId}
        columns={userColumns}
        open={uniqueOpen}
        onOpenChange={setUniqueOpen}
      />
    </div>
  );
}

/** Friendly labels for the backend data-type ids in the type picker — the
 *  physical names (e.g. ``timestamptz``) are cryptic for an app builder. */
const DATA_TYPE_LABELS: Record<string, string> = {
  text: "text",
  integer: "integer (number)",
  bigint: "bigint (large number)",
  numeric: "numeric (decimal)",
  boolean: "boolean (yes/no)",
  date: "date",
  timestamptz: "datetime (date + time)",
  select: "select (dropdown)",
  multiselect: "multi-select (chips)",
  member: "member (assignee)",
  file: "file (attachment)",
  jsonb: "json",
  uuid: "uuid",
};

/** The full add-a-column dialog (name + type + per-kind config). Exported so the
 *  ER diagram can reuse the exact same field-creation UI inline. */
export function AddColumnDialog({
  appId,
  tableId,
  open,
  onOpenChange,
}: {
  appId: string;
  tableId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const typesQ = useQuery({
    queryKey: ["apps", "data-types"],
    queryFn: () => appsApi.dataTypes(),
    enabled: open,
  });
  // For a relation column: the app's other tables to point at.
  const tablesQ = useQuery({
    queryKey: ["apps", appId, "tables"],
    queryFn: () => appsApi.listTables(appId),
    enabled: open,
  });
  // This table's columns — relation columns power the lookup picker.
  const thisTableQ = useQuery({
    queryKey: ["apps", appId, "tables", tableId],
    queryFn: () => appsApi.getTable(appId, tableId),
    enabled: open,
  });

  const [name, setName] = React.useState("");
  const [dataType, setDataType] = React.useState("");
  const [required, setRequired] = React.useState(false);
  // Newline-separated choices for a `select` column (one option per line).
  const [optionsText, setOptionsText] = React.useState("");
  // Target table id for a `relation` column.
  const [targetTable, setTargetTable] = React.useState("");
  // What happens to children when their parent row is deleted (D19).
  const [onDelete, setOnDelete] = React.useState("restrict");
  // `lookup`: pull <field> across the relation column <relation>.
  const [lookupRelation, setLookupRelation] = React.useState("");
  const [lookupField, setLookupField] = React.useState("");
  // `rollup`: aggregate child rows of <source> via <via>.
  const [rollupSource, setRollupSource] = React.useState("");
  const [rollupVia, setRollupVia] = React.useState("");
  const [rollupOp, setRollupOp] = React.useState("sum");
  const [rollupField, setRollupField] = React.useState("");
  const [rollupFilters, setRollupFilters] = React.useState<FilterRow[]>([]);
  // `formula`: a per-row JS expression over the row's own fields + result type.
  const [formulaExpression, setFormulaExpression] = React.useState("");
  const [formulaResultType, setFormulaResultType] = React.useState("numeric");

  React.useEffect(() => {
    if (open) {
      setName("");
      setDataType("");
      setRequired(false);
      setOptionsText("");
      setTargetTable("");
      setOnDelete("restrict");
      setLookupRelation("");
      setLookupField("");
      setRollupSource("");
      setRollupVia("");
      setRollupOp("sum");
      setRollupField("");
      setRollupFilters([]);
      setFormulaExpression("");
      setFormulaResultType("numeric");
    }
  }, [open]);

  // Relation columns of THIS table (for the lookup's "via relation" picker).
  const myRelations = (thisTableQ.data?.columns ?? []).filter((c) => c.kind === "relation");
  const lookupTargetId = myRelations.find((c) => c.name === lookupRelation)?.config?.target_table as
    | string
    | undefined;
  // Columns of the lookup target table (its fields are the pull options).
  const lookupTargetQ = useQuery({
    queryKey: ["apps", appId, "tables", lookupTargetId],
    queryFn: () => appsApi.getTable(appId, lookupTargetId!),
    enabled: open && !!lookupTargetId,
  });
  // Columns of the rollup source table (for the via / field / filter pickers).
  const rollupSourceQ = useQuery({
    queryKey: ["apps", appId, "tables", rollupSource],
    queryFn: () => appsApi.getTable(appId, rollupSource),
    enabled: open && !!rollupSource,
  });
  const sourceCols = rollupSourceQ.data?.columns ?? [];
  // Relation columns on the source that point back at THIS table.
  const backRefs = sourceCols.filter(
    (c) => c.kind === "relation" && c.config?.target_table === tableId,
  );

  // De-duplicated, trimmed, non-empty option list for a select column.
  const options = React.useMemo(() => {
    const seen = new Set<string>();
    return optionsText
      .split("\n")
      .map((o) => o.trim())
      .filter((o) => o.length > 0 && !seen.has(o) && seen.add(o));
  }, [optionsText]);

  // Default the type select to the first catalog entry once loaded.
  React.useEffect(() => {
    if (open && !dataType && typesQ.data && typesQ.data.length > 0) {
      setDataType(typesQ.data[0].id);
    }
  }, [open, dataType, typesQ.data]);

  const create = useMutation({
    mutationFn: (body: ColumnIn) => appsApi.addColumn(appId, tableId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["apps", appId, "tables", tableId] });
      onOpenChange(false);
    },
  });

  /** Build the create-column payload for the chosen kind/type. */
  function buildColumnBody(): ColumnIn {
    return buildColumnPayload({
      name,
      dataType,
      required,
      options,
      targetTable,
      onDelete,
      lookupRelation,
      lookupField,
      rollupSource,
      rollupVia,
      rollupOp,
      rollupField,
      rollupFilters,
      formulaExpression,
      formulaResultType,
    });
  }

  const isSelect = dataType === "select";
  const isMultiselect = dataType === "multiselect";
  const isEnum = isSelect || isMultiselect; // both carry an options list
  const isRelation = dataType === "relation";
  const isRelationMany = dataType === "relation_many";
  const isLookup = dataType === "lookup";
  const isRollup = dataType === "rollup";
  const isFormula = dataType === "formula";
  const canSubmit =
    name.trim().length > 0 &&
    dataType.length > 0 &&
    (!isEnum || options.length > 0) &&
    (!(isRelation || isRelationMany) || targetTable.length > 0) &&
    (!isLookup || (lookupRelation.length > 0 && lookupField.length > 0)) &&
    (!isRollup ||
      (rollupSource.length > 0 &&
        rollupVia.length > 0 &&
        (rollupOp === "count" || rollupField.length > 0))) &&
    (!isFormula || formulaExpression.trim().length > 0) &&
    !create.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("apps.addColumn", "Add column")}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="col-name">{t("apps.columnName", "Name")}</Label>
            <Input
              id="col-name"
              value={name}
              autoFocus
              placeholder="title"
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="col-type">{t("apps.dataType", "Type")}</Label>
            <select
              id="col-type"
              value={dataType}
              onChange={(e) => setDataType(e.target.value)}
              className="block w-full rounded-md border border-border bg-surface px-2 py-1.5 text-sm"
            >
              {(typesQ.data ?? []).map((dt) => (
                <option key={dt.id} value={dt.id}>
                  {DATA_TYPE_LABELS[dt.id] ?? dt.id}
                </option>
              ))}
              <option value="relation">relation (link to one record)</option>
              <option value="relation_many">relation_many (link to many records)</option>
              <option value="lookup">lookup (pull from a linked record)</option>
              <option value="rollup">rollup (aggregate child records)</option>
              <option value="formula">formula (calculate per row)</option>
            </select>
          </div>
          {isLookup && (
            <div className="space-y-3 rounded-md border border-border/60 bg-surface/40 p-3">
              <div className="space-y-1.5">
                <Label htmlFor="lk-rel">{t("apps.lookupVia", "Via relation")}</Label>
                <select
                  id="lk-rel"
                  value={lookupRelation}
                  onChange={(e) => {
                    setLookupRelation(e.target.value);
                    setLookupField("");
                  }}
                  className="block w-full rounded-md border border-border bg-surface px-2 py-1.5 text-sm"
                >
                  <option value="">{t("apps.selectRelation", "Select a relation column…")}</option>
                  {myRelations.map((c) => (
                    <option key={c.id} value={c.name}>
                      {c.name}
                    </option>
                  ))}
                </select>
                {myRelations.length === 0 && (
                  <p className="text-[11px] text-warning">
                    {t("apps.noRelationCols", "Add a relation column first.")}
                  </p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="lk-field">{t("apps.lookupField", "Field to pull")}</Label>
                <select
                  id="lk-field"
                  value={lookupField}
                  disabled={!lookupRelation}
                  onChange={(e) => setLookupField(e.target.value)}
                  className="block w-full rounded-md border border-border bg-surface px-2 py-1.5 text-sm disabled:opacity-50"
                >
                  <option value="">{t("apps.selectField", "Select a field…")}</option>
                  {(lookupTargetQ.data?.columns ?? []).map((c) => (
                    <option key={c.id} value={c.name}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
          {isRollup && (
            <div className="space-y-3 rounded-md border border-border/60 bg-surface/40 p-3">
              <div className="space-y-1.5">
                <Label htmlFor="ru-src">{t("apps.rollupSource", "Child table")}</Label>
                <select
                  id="ru-src"
                  value={rollupSource}
                  onChange={(e) => {
                    setRollupSource(e.target.value);
                    setRollupVia("");
                    setRollupField("");
                    setRollupFilters([]);
                  }}
                  className="block w-full rounded-md border border-border bg-surface px-2 py-1.5 text-sm"
                >
                  <option value="">{t("apps.selectTable", "Select a table…")}</option>
                  {(tablesQ.data ?? []).map((tb) => (
                    <option key={tb.id} value={tb.id}>
                      {tb.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ru-via">{t("apps.rollupVia", "Linked back via")}</Label>
                <select
                  id="ru-via"
                  value={rollupVia}
                  disabled={!rollupSource}
                  onChange={(e) => setRollupVia(e.target.value)}
                  className="block w-full rounded-md border border-border bg-surface px-2 py-1.5 text-sm disabled:opacity-50"
                >
                  <option value="">{t("apps.selectRelation", "Select a relation column…")}</option>
                  {backRefs.map((c) => (
                    <option key={c.id} value={c.name}>
                      {c.name}
                    </option>
                  ))}
                </select>
                {rollupSource && backRefs.length === 0 && (
                  <p className="text-[11px] text-warning">
                    {t("apps.noBackRef", "That table has no relation pointing here.")}
                  </p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="ru-op">{t("apps.rollupOp", "Aggregate")}</Label>
                  <select
                    id="ru-op"
                    value={rollupOp}
                    onChange={(e) => setRollupOp(e.target.value)}
                    className="block w-full rounded-md border border-border bg-surface px-2 py-1.5 text-sm"
                  >
                    {["count", "sum", "avg", "min", "max"].map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                </div>
                {rollupOp !== "count" && (
                  <div className="space-y-1.5">
                    <Label htmlFor="ru-field">{t("apps.rollupField", "Of field")}</Label>
                    <select
                      id="ru-field"
                      value={rollupField}
                      disabled={!rollupSource}
                      onChange={(e) => setRollupField(e.target.value)}
                      className="block w-full rounded-md border border-border bg-surface px-2 py-1.5 text-sm disabled:opacity-50"
                    >
                      <option value="">{t("apps.selectField", "Select a field…")}</option>
                      {sourceCols
                        .filter((c) => c.kind === "data")
                        .map((c) => (
                          <option key={c.id} value={c.name}>
                            {c.name}
                          </option>
                        ))}
                    </select>
                  </div>
                )}
              </div>
              <div className="space-y-1.5">
                <Label>{t("apps.rollupFilter", "Only where (optional)")}</Label>
                <FilterRowsEditor
                  columns={sourceCols.filter((c) => c.kind === "data").map((c) => c.name)}
                  value={rollupFilters}
                  onChange={setRollupFilters}
                  disabled={!rollupSource}
                />
                <p className="text-[11px] text-muted-foreground">
                  {t(
                    "apps.rollupFilterHint",
                    "All filters must match (AND) for a child row to count.",
                  )}
                </p>
              </div>
            </div>
          )}
          {isFormula && (
            <div className="space-y-3 rounded-md border border-border/60 bg-surface/40 p-3">
              <div className="space-y-1.5">
                <Label htmlFor="fx-expr">{t("apps.formulaExpression", "Expression")}</Label>
                <textarea
                  id="fx-expr"
                  rows={2}
                  value={formulaExpression}
                  placeholder="$json.qty * $json.price"
                  onChange={(e) => setFormulaExpression(e.target.value)}
                  className="block w-full resize-y rounded-md border border-border bg-background px-2 py-1.5 font-mono text-xs"
                />
                <p className="text-[11px] text-muted-foreground">
                  {t(
                    "apps.formulaHint",
                    "A per-row JavaScript expression over this row's fields ($json.<column>). Spreadsheet functions available: SUM, IF, ROUND, CONCAT, FORMATDATE…",
                  )}
                </p>
                {(thisTableQ.data?.columns ?? []).filter((c) => c.kind === "data").length > 0 && (
                  <p className="text-[11px] text-muted-foreground">
                    {t("apps.formulaFields", "Fields:")}{" "}
                    <span className="font-mono">
                      {(thisTableQ.data?.columns ?? [])
                        .filter((c) => c.kind === "data")
                        .map((c) => `$json.${c.name}`)
                        .join(", ")}
                    </span>
                  </p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="fx-type">{t("apps.formulaResultType", "Result type")}</Label>
                <select
                  id="fx-type"
                  value={formulaResultType}
                  onChange={(e) => setFormulaResultType(e.target.value)}
                  className="block w-full rounded-md border border-border bg-surface px-2 py-1.5 text-sm"
                >
                  {["numeric", "integer", "text", "boolean", "date"].map((rt) => (
                    <option key={rt} value={rt}>
                      {rt}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
          {(isRelation || isRelationMany) && (
            <div className="space-y-1.5">
              <Label htmlFor="col-target">{t("apps.relationTarget", "Linked table")}</Label>
              <select
                id="col-target"
                value={targetTable}
                onChange={(e) => setTargetTable(e.target.value)}
                className="block w-full rounded-md border border-border bg-surface px-2 py-1.5 text-sm"
              >
                <option value="">{t("apps.selectTable", "Select a table…")}</option>
                {(tablesQ.data ?? []).map((tb) => (
                  <option key={tb.id} value={tb.id}>
                    {tb.name}
                  </option>
                ))}
              </select>
              <p className="text-[11px] text-muted-foreground">
                {isRelationMany
                  ? t("apps.relationManyHelp", "Each row links to many rows of the chosen table.")
                  : t("apps.relationHelp", "Each row links to one row of the chosen table.")}
              </p>
              {isRelation && (
                <div className="space-y-1.5 pt-1">
                  <Label htmlFor="col-ondelete">
                    {t("apps.onDelete", "When the linked row is deleted")}
                  </Label>
                  <select
                    id="col-ondelete"
                    value={onDelete}
                    onChange={(e) => setOnDelete(e.target.value)}
                    className="block w-full rounded-md border border-border bg-surface px-2 py-1.5 text-sm"
                  >
                    <option value="restrict">
                      {t("apps.onDeleteRestrict", "Restrict — block the deletion")}
                    </option>
                    <option value="set_null">
                      {t("apps.onDeleteSetNull", "Clear — set this link to empty")}
                    </option>
                    <option value="cascade">
                      {t("apps.onDeleteCascade", "Cascade — delete this row too")}
                    </option>
                  </select>
                </div>
              )}
            </div>
          )}
          {isEnum && (
            <div className="space-y-1.5">
              <Label htmlFor="col-options">{t("apps.selectOptions", "Options (one per line)")}</Label>
              <textarea
                id="col-options"
                value={optionsText}
                rows={4}
                placeholder={"new\nnegotiation\nwon\nlost"}
                className="block w-full rounded-md border border-border bg-surface px-2 py-1.5 font-mono text-sm"
                onChange={(e) => setOptionsText(e.target.value)}
              />
              <p className="text-[11px] text-muted-foreground">
                {t(
                  "apps.selectOptionsHelp",
                  "The allowed choices for this column. Rendered as a dropdown.",
                )}
              </p>
            </div>
          )}
          <div className="flex items-center justify-between">
            <Label htmlFor="col-required">{t("apps.required", "Required")}</Label>
            <Switch id="col-required" checked={required} onCheckedChange={setRequired} />
          </div>
          {create.isError && (
            <Alert tone="danger" title={t("apps.addColumnFailed", "Could not add column")}>
              <span className="font-mono text-xs">{(create.error as Error).message}</span>
            </Alert>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button disabled={!canSubmit} onClick={() => create.mutate(buildColumnBody())}>
            {t("apps.add", "Add")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddUniqueSetDialog({
  appId,
  tableId,
  columns,
  open,
  onOpenChange,
}: {
  appId: string;
  tableId: string;
  columns: ColumnOut[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const [name, setName] = React.useState("");
  // Ordered list (NOT a Set) so composite-key column order = click order.
  const [selected, setSelected] = React.useState<string[]>([]);

  React.useEffect(() => {
    if (open) {
      setName("");
      setSelected([]);
    }
  }, [open]);

  const create = useMutation({
    mutationFn: (body: UniqueSetIn) => appsApi.createUniqueSet(appId, tableId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["apps", appId, "tables", tableId] });
      onOpenChange(false);
    },
  });

  const toggle = (id: string) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const canSubmit = name.trim().length > 0 && selected.length > 0 && !create.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("apps.addUniqueSet", "Add constraint")}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="set-name">{t("apps.constraintName", "Name")}</Label>
            <Input
              id="set-name"
              value={name}
              autoFocus
              placeholder="by_email"
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label>{t("apps.columns", "Columns")}</Label>
            <div className="max-h-48 space-y-1 overflow-auto rounded-md border border-border/60 p-2">
              {columns.map((c) => (
                <label
                  key={c.id}
                  className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-sm hover:bg-muted/50"
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(c.id)}
                    onChange={() => toggle(c.id)}
                  />
                  <span>{c.name}</span>
                  <span className="font-mono text-[11px] text-muted-foreground">{c.data_type}</span>
                </label>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground">
              {t(
                "apps.uniqueSetHint",
                "Order matters for composite keys; selection order is preserved.",
              )}
            </p>
          </div>
          {create.isError && (
            <Alert tone="danger" title={t("apps.addUniqueSetFailed", "Could not add constraint")}>
              <span className="font-mono text-xs">{(create.error as Error).message}</span>
            </Alert>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            disabled={!canSubmit}
            onClick={() =>
              create.mutate({
                name: name.trim(),
                // Preserve selection order: filter the columns list by membership.
                column_ids: selected,
              })
            }
          >
            {t("apps.add", "Add")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
